'use strict';

import Constants, { headers as HEADERS } from '@uw-it-sis/lib-js-common/lib/Constants';

/*
 * A basic http client class.
 *
 * This class will send generic http requests and do some basic processing/logging/error handling
 *
 * An error will be thrown on unsuccessful http requests.
 */
class HttpClient {

    /*
     * Construct an HttpClient with the given items
     */
    constructor(appStore, exceptionUtils) {
        this.appStore = appStore;
        this.exceptionUtils = exceptionUtils;
    }

    /**
     * The main sendRequest method.
     *
     * @param {string} type - The type of request to send (GET,POST,PUT,DELETE,etc)
     * @param {string} url - The url to request
     * @param {object|string} [body] - The post body to send
     * @param {object} [headers] - An object of custom header key/values
     * @param {string} [operationName] - A description of this request to use for error logging
     * @param {boolean} [skipExceptionHandling] - A flag to indicate that the client wants to handle the exception and
     *                                            that "generic" error handling should be skipped.
     */
    async sendRequest(type, url, body, headers, operationName, skipExceptionHandling = false) {

        /*
         * PREP
         */

        // Normalize type ...
        let method = type.toUpperCase();

        /** @type {RequestInit} */
        let fetchConfig = {
            credentials: "include",
            method: method,
            headers: this._buildHeaders({ method, headers }),
        }

        // If a body was given and is a vanilla object or array, convert it to a string
        if (body) {
            let typeCheck = Object.prototype.toString.call(body)
            if (typeof body === 'string') {
                fetchConfig.body = body
            } else if (typeCheck === '[object Object]' || typeCheck === '[object Array]') {
                fetchConfig.body = JSON.stringify(body);
            } else {
                /*
                 * This is something else so let fetch set the content-type header automatically.
                 * For example, if you are posting a FormData object fetch needs to set the content-type header since
                 * there is some extra boundary data that needs to go into the header that can't be known ahead of time.
                 * e.g. that might look like:
                 * content-type: multipart/form-data; boundary=---------------------------42506699884249063733927479238
                 * where the boundary is a divider between different files/form things inside of the payload.
                 */
                delete fetchConfig.headers['Content-Type']
                fetchConfig.body = body
            }
        }

        // Our cloud apps use a different domain than the UI is served from, so use that if it is available.
        if (this.appStore.getApplicationApiUrl()) {
            url = this.appStore.getApplicationApiUrl() + "/" + url;
        }

        /*
         * SEND THE ACTUAL HTTP REQUEST
         */
        let response = await fetch(url, fetchConfig);

        /*
         * POST-PROCESSING
         */
        // Output some debugging info on errors.
        if (! response.ok && this.appStore.isDevMode()) {
            console.log(`Response [${response.status}][${response.statusText}].`);
        }

        // We have some extra steps for handling special responses
        if (response.status === 401) {
            // This means the user needs to refresh their session token
            this.appStore.doLogin();
            throw 401;
        } else if (response.status === 409) {
            // This means the app-api has been updated since the user session
            // started, suggesting the UI is likely out of date, so the client
            // should refresh.
            this.appStore.doRefresh();
            throw 409;
        } else if (response.status === 503) {
            // This means the application is undergoing maintenance
            this.appStore.setMaintenanceMode();
        }

        // Parse the Response body
        // ResponseBody will be the empty string if the response has no body
        let responseBody = await response.text();
        responseBody = this._parseResponseBody(response, responseBody);

        /*
         * Look for error responses from the api
         */
        try {
            // Handle error object from the API gateway.
            if (responseBody.hasOwnProperty("error")) {
                throw responseBody;
            }

            // Look for an error response from our controllers.
            if (responseBody.exceptionCode) {
                throw responseBody;
            }

            // Look for any general errors. Server errors are usually just text, so won't have any error props.
            if (!response.ok) {
                throw responseBody;
            }
        } catch (error) {
            if (! skipExceptionHandling) {
                this.exceptionUtils.handleException(error, operationName);
            }
            // re-throw error to tell client something went wrong and for further handling.
            throw error;
        }
        return responseBody;
    }

    /****************************
     * Friendly request methods
     */

    /**
     * Performs and http GET.
     * @param url The URL to call.
     * @param operationName The operation name being performed. This is only for debugging.
     * @param skipExceptionHandling If true error handling left completely to the caller.
     * @param callbackName If supplied the request will be made asynchronously and the response be delivered via a websockets
     *                   connection. You shouldn't supply this arg unless asyncEnabled is true in your app config.
     * @return {Promise<any>}
     */
    doGet(url, operationName, skipExceptionHandling, callbackName) {
        let headers;
        if (callbackName) {
            headers = this._buildAsyncHeader(callbackName);
        }
        return this.sendRequest('GET', url, null, headers, operationName, skipExceptionHandling);
    }

    /**
     * Performs and http DELETE.
     * @param url The URL to call.
     * @param operationName The operation name being performed. This is only for debugging.
     * @param skipExceptionHandling If true error handling left completely to the caller.
     * @param callbackName If supplied the request will be made asynchronously and the response be delivered via a websockets
     *                   connection. You shouldn't supply this arg unless asyncEnabled is true in your app config.
     * @return {Promise<any>}
     */
    doDelete(url, operationName, skipExceptionHandling, callbackName) {
        let headers;
        if (callbackName) {
            headers = this._buildAsyncHeader(callbackName);
        }
        return this.sendRequest('DELETE', url, null, headers, operationName, skipExceptionHandling);
    }

    /**
     * Performs and http POST.
     * @param url The URL to call.
     * @param body The request body.
     * @param operationName The operation name being performed. This is only for debugging.
     * @param skipExceptionHandling If true error handling left completely to the caller.
     * @param callbackName If supplied the request will be made asynchronously and the response be delivered via a websockets
     *                   connection. You shouldn't supply this arg unless asyncEnabled is true in your app config.
     * @return {Promise<any>}
     */
    doPost(url, body, operationName, skipExceptionHandling, callbackName) {
        let headers;
        if (callbackName) {
            headers = this._buildAsyncHeader(callbackName);
        }
        return this.sendRequest('POST', url, body, headers, operationName, skipExceptionHandling);
    }

    /**
     * Performs and http PUT.
     * @param url The URL to call.
     * @param body The request body.
     * @param operationName The operation name being performed. This is only for debugging.
     * @param skipExceptionHandling If true error handling left completely to the caller.
     * @param callbackName If supplied the request will be made asynchronously and the response be delivered via a websockets
     *                   connection. You shouldn't supply this arg unless asyncEnabled is true in your app config.
     * @return {Promise<any>}
     */
    doPut(url, body, operationName, skipExceptionHandling, callbackName) {
        let headers;
        if (callbackName) {
            headers = this._buildAsyncHeader(callbackName);
        }
        return this.sendRequest('PUT', url, body, headers, operationName, skipExceptionHandling);
    }

    /**
     * Performs and http PATCH.
     * @param url The URL to call.
     * @param body The request body.
     * @param operationName The operation name being performed. This is only for debugging.
     * @param skipExceptionHandling If true error handling left completely to the caller.
     * @param callbackName If supplied the request will be made asynchronously and the response be delivered via a websockets
     *                   connection. You shouldn't supply this arg unless asyncEnabled is true in your app config.
     * @return {Promise<any>}
     */
    doPatch(url, body, operationName, skipExceptionHandling, callbackName) {
        let headers;
        if (callbackName) {
            headers = this._buildAsyncHeader(callbackName);
        }
        return this.sendRequest('PATCH', url, body, headers, operationName, skipExceptionHandling);
    }

    /**
     * A convenience method to put together the value for an async callback header.
     * @param callbackName The name of the callback (e.g. getRegData).
     * @return {string} The value of an async header.
     * @private
     */
    _buildAsyncHeader(callbackName) {
        let asyncHeaders = {};
        asyncHeaders[Constants.headers.asyncCallback] = callbackName;
        return asyncHeaders;
    }

    /**
     * Generate the headers to send with the request.
     * Given headers will override default headers.
     * @param {object} args
     * @param {string} args.method - The type of request (GET,POST,PUT,etc)
     * @param {object} args.headers - Custom headers to send with this request
     */
    _buildHeaders({method, headers}) {
        let defaultHeaders = {};

        // There is a CORS issue in the App API routing code when setting Content-Type (and also setting it on methods without
        // bodies doesn't really make sense), so conditionally set the content-type header.
        switch (method) {
            case "PUT":
            case "POST":
            case "PATCH":
            case "DELETE":
                defaultHeaders['Content-Type'] = "application/json"
                break;
            default:
                break;
        }

        // Add the CSRF token to ALL requests except "csrf safe methods" (GET, HEAD, and OPTIONS).
        // Based on advice from OWASP, this is the preferred way to set CSRF tokens, rather than
        // just setting on the known "state changing" methods (POST, PUT, PATCH, DELETE). This is
        // the same logic we use on the backend.
        let csrfToken = this.appStore.getCSRFToken();
        if (csrfToken) {
            switch (method) {
                case "GET":
                case "HEAD":
                case "OPTIONS":
                    break;
                default:
                    defaultHeaders[HEADERS.csrf] = csrfToken;
                    break;
            }
        }

        // Add checksum header if available
        let checksum = this.appStore.getChecksum();
        if (checksum) {
            defaultHeaders[HEADERS.apichecksum] = checksum;
        }

        return {
            ...defaultHeaders,
            ...headers,
        };
    }

    /**
     * If the response has content-type application/json, parse it into json, otherwise return
     * the response text.
     */
    _parseResponseBody(response, responseBody) {
        let contentType = response.headers.get('content-type');
        // careful not to parse the empty string that's sometimes returned on DELETEs
        if (responseBody && contentType.split(';').includes('application/json')) {
            return JSON.parse(responseBody);
        } else {
            return responseBody
        }
    }

    /**
     * This looks for any potential errors with the response and throws if there are.
     */
    _validateResponse(response, body) {
        // On a 401 response, the user needs to log in
        if (response.status === 401) {
            throw 401;
        }

        // On a 409 response, the UI needs to be refreshed
        if (response.status === 409) {
            throw 409;
        }

        // Handle error object from the API gateway.
        if (body.hasOwnProperty("error")) {
            throw body;
        }

        // Look for an error response from our controllers.
        if (body.exceptionCode) {
            throw body;
        }

        // Look for any general errors. Server errors are usually just text, so won't have any error props.
        if (!response.ok) {
            throw body;
        }
    };

    /*
     * Process redirect responses.
     */
    _handleRedirect(response) {
        // See if this is a redirect object.
        if (response.redirectUrl) {
            const currentPath = this.appStore.getCurrentPath();
            if (response.redirectUrl.startsWith(`/${this.appStore.getAppContext()}/api/session`)) {
                /*
                 * This is the server side letting the app know that it needs to request a session.
                 * Instead of following this redirect just have appStore request the session.
                 */
                this.appStore.mount();
            } else {
                // Otherwise, just follow the redirect, but add a 'referrer' param so that the present context can be preserved.
                let url = `${response.redirectUrl}?referrer=${currentPath}`;
                this.appStore.redirect(url);
            }
        } else {
            /*
             * Otherwise, if no specific redirect location was specified, then throwing 401 will prompt the exception
             * handler (usually ExceptionUtils.handleException()) to have AppStore present the Disco component.
             */
            throw 401;
        }
    };
}

export default HttpClient;
